// ÉRDEMI TARTALOM egy naplóbejegyzésben — mikor mentheto egyáltalán.
//
// Miért van ez a fájl (2026-09-15, D2 döntés, ClickUp 123qmzqyamj): a súly
// eddig FELTÉTEL NÉLKÜL kötelező volt (validateWeight üres értékre hibát ad,
// és a Naplo.tsx mindhárom mentési pontja meghívta). A felhasználói minta
// viszont más: a bejegyzések ~75%-a visszamenőleges, és a dózis-előzményt
// gyakran súly nélkül viszik fel — nem emlékeznek, mennyi volt akkor a súly.
//
// Ráadásul a mai állapot nem szigorú validáció, hanem CSAPDA: a mező
// opcionálisnak látszik (az élő hibaüzenet üres értékre null, a Mentés gomb
// sem tiltódik), aztán a mentés hasal el.
//
// Az új szabály: ha van dózis VAGY súly VAGY bármely más érdemi mező, a
// mentés megy.

import { validateWeight } from "./weightValidation";
import { validateEntryDate } from "./dateValidation";

export interface EntryContentInput {
  weight: number | "";
  /** A ténylegesen mentendő dózis (a custom-mező feloldása után). */
  dose: number | "";
  glucose: number | "";
  glucoseEnabled: boolean;
  mellekhat: Record<string, number>;
}

/**
 * Van-e a bejegyzésben legalább egy érdemi adat.
 *
 * ⚠️ A `mood` és az `appetite` SZÁNDÉKOSAN nem számít bele. Mindkettő
 * alapértéke 3 (a skála közepe), és a mentéskor mindig kimegy — tehát
 * mindig „ki van töltve", és semmilyen jelet nem hordoz arról, hogy a
 * felhasználó ténylegesen megadott-e valamit. Ha beszámítanánk őket, a
 * függvény mindig true-t adna, és az ellenőrzés értelmét vesztené.
 *
 * A `date` szintén kimarad: az a bejegyzés NAPJA, nem tartalom — mindig van.
 */
export function hasMeaningfulEntryContent(input: EntryContentInput): boolean {
  if (input.weight !== "") return true;

  // A dózis lehet "" (nincs megadva) vagy 0 (a „nincs injekció" jelölése) —
  // egyik sem érdemi tartalom. Csak a pozitív dózis az.
  if (typeof input.dose === "number" && input.dose > 0) return true;

  // A vércukor csak akkor számít, ha a modul be van kapcsolva — különben a
  // mező nem is jelenik meg, és a mentendő payloadba sem kerül bele.
  if (input.glucoseEnabled && input.glucose !== "") return true;

  if (Object.keys(input.mellekhat).length > 0) return true;

  return false;
}

/** Egységes hibaüzenet, ha nincs érdemi tartalom. */
export const NO_CONTENT_MESSAGE =
  "Adj meg legalább egy adatot — súlyt, dózist vagy mellékhatást.";

/**
 * A súly mentendő alakja.
 *
 * ⚠️ Miért kell ez külön függvény (2026-09-15, QA N1): a form-state típusa
 * `number | ""`, és az üres stringet NEM lehet kiküldeni. A
 * `firestore.rules` `validWeight`-je a HIÁNYZÓ és a NULL mezőt engedi, az
 * üres stringet nem — arra mindhárom ága hamis:
 *   'weight' in data  → igaz (a mező ott van)
 *   data.weight == null → hamis (az üres string nem null)
 *   data.weight is number → hamis
 *
 * Emiatt a súly nélküli mentés a SZABÁLYON hasalt el, hiába engedte a
 * kliensoldali ellenőrzés. A rules-teszt `delete weight`-et és `null`-t
 * vizsgált, a kliens-teszt form-state-et — a kettő közötti illesztést
 * egyik sem. Ez a függvény az az illesztés, egy helyen.
 *
 * A `glucose` ugyanezt a normalizálást végzi inline a payloadban.
 */
export function normalizeWeightForWrite(weight: number | ""): number | null {
  return weight === "" ? null : weight;
}

/** Egy visszamenőleges előzmény-sor az onboardingban (mind string, űrlapból). */
export interface HistoryRowInput {
  date: string;
  weight: string;
  dose: string;
}

/**
 * Menthető-e egy onboarding retro-előzmény sor.
 *
 * ⚠️ 2026-09-15 (D2 döntés befejezése): korábban a szűrés `r.date &&
 * r.weight && ...` volt, tehát a SÚLYT KÖTELEZŐVÉ tette — és egy
 * dózis-only sor csendben kiesett. Épp az a use-case, amiért a D2
 * döntés született: a visszatérő felhasználó viszi fel a
 * dózis-előzményét, gyakran súly nélkül.
 *
 * Az új szabály megegyezik a Napló mentési szabályával: dátum kell, és
 * legalább egy érdemi mező (súly VAGY dózis). Ha van súly, továbbra is
 * érvényesnek kell lennie — a jelenlét-követelmény oldódott, az
 * érvényességi nem.
 */
export function isSavableHistoryRow(row: HistoryRowInput): boolean {
  // 2026-09-25 (ClickUp 123qmzqz0q4): a dátum JELENLÉTE eddig is feltétel
  // volt, az ÉRVÉNYESSÉGE nem — egy elgépelt évszámú retro sor simán
  // bekerült. Ugyanaz a bontás, mint a súlynál: a jelenlét és az
  // érvényesség két külön kérdés, és mindkettőt itt kell eldönteni, ne
  // csak a UI-n múljon.
  //
  // ⚠️ A validateEntryDate a Napló sávját engedi (+30 nap). Az onboarding
  // dátum-mezői ennél SZIGORÚBBAK (max={ma}), mert a terápia kezdete és a
  // visszamenőleges előzmény definíció szerint múlt — ez a függvény a
  // padló, nem a plafon.
  if (validateEntryDate(row.date)) return false;
  if (!row.weight && !row.dose) return false;
  if (row.weight && validateWeight(row.weight)) return false;
  return true;
}
